import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { FieldSize } from '@/enums/system/FieldSize';
import { PropType, computed, watch, ref } from 'vue';
import SingleSelect from '../single-select.vue';
import { IDDescriptionPair } from '@/types/generic/IDDescriptionPair';
import { useSupportCaseReasonsStore } from '@/stores/utility-switching/SupportCaseReasonsStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'secondary-support-case-reasons-single-select',
  props: {
    /** 
     * The numeric value bound to `v-model`.
     * Typically represents an ID or a selected value within the component.
     * @type {number}
     * @required
     */
    modelValue: {
        type: Number,
        required: true
    },

    /** 
     * The ID of the primary support case reason.
     * Used to associate the component with a specific case reason.
     * @type {number}
     * @required
     */
    primarySupportCaseReasonId: {
        type: Number,
        required: true
    },

    /** 
     * The label displayed for the control.
     * Provides a description or title for the input field.
     * @type {string}
     * @default ''
     */
    label: {
        type: String,
        default: ''
    },

    /** 
     * The width of the field, defined by the `FieldSize` enum.
     * Controls the display size of the input component.
     * @type {FieldSize}
     * @default FieldSize.Full
     */
    width: {
        type: String as PropType<FieldSize>,
        default: FieldSize.Full
    },

    /** 
     * Determines whether the control is read-only.
     * If `true`, the input is disabled, preventing user modifications.
     * @type {boolean}
     * @default false
     */
    readOnly: {
        type: Boolean,
        default: false
    },

    /** 
     * The validation group name used for form validation.
     * Helps group validation rules within a form.
     * @type {string}
     * @default ''
     */
    validationGroup: {
        type: String,
        default: ''
    }
},
  emits: [
    /** 
     * Emitted when the `modelValue` is updated, typically triggered when a selection changes.
     * @event update:modelValue
     * @param {Number} value - The new value to be updated in the model.
     */
    'update:modelValue',
],
  setup(__props, { emit }) {

const props = __props;






const selectedReasonId = computed({
    get: () => props.modelValue,
    set: (value: number) => emit('update:modelValue', value)
});

const supportReasonsCasesStore = useSupportCaseReasonsStore();
const supportCaseReasons = ref<IDDescriptionPair[]>([]);
const retrievingData = ref(false);

const singleSelectReadOnly = computed(() => retrievingData.value || props.readOnly);

// When primary case reason changes, re-populate the secondary support case reasons
watch(() => props.primarySupportCaseReasonId, async () => {
    retrievingData.value = true

    supportCaseReasons.value = await supportReasonsCasesStore.getSecondarySupportCaseReasons(props.primarySupportCaseReasonId);

    // Reset selected reason id to improve UX and to avoid invalid items being selected
    selectedReasonId.value = supportCaseReasons.value?.length ? supportCaseReasons.value[0].id : -1;

    retrievingData.value = false;
}, { immediate: true });


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SingleSelect, {
    modelValue: selectedReasonId.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedReasonId).value = $event)),
    "data-source": supportCaseReasons.value,
    width: __props.width,
    label: __props.label,
    showClearButton: false,
    "read-only": singleSelectReadOnly.value,
    "allow-zero-value": true,
    "validation-group": __props.validationGroup
  }, null, 8, ["modelValue", "data-source", "width", "label", "read-only", "validation-group"]))
}
}

})